import { Component, DestroyRef, inject, LOCALE_ID, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { ProfileCreateModel } from 'src/app/common/ApplicationUserProfileForCreation';
import { AppProfileService } from 'src/app/services/profile.service';
import { Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { AuthorizeService } from 'src/app/auth/authorize.service';
import { EMPTY, forkJoin, Observable, take, tap } from 'rxjs';
import { CountryService } from '../../services/country.service';
import { CountryModel } from '../../common/Country';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { IUser } from '../../common/IUser.interface';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { salutationCodeValidator, salutationOptionsFor } from '../../common/Salutation';
import { CountryItem, toCountryItems } from '../../common/country-name';
import { FormRowComponent } from '../form-row/form-row.component';
import { errorsDue, FieldErrorsComponent } from '../form-row/field-errors.component';
import { submittable } from '../../common/server-field-errors';

/** The registration form. Its type is inferred from here (#199). */
function buildRegistrationForm(fb: FormBuilder,
  data: {
    appProfile: ProfileCreateModel;
    email: string;
    subject: string;
    tac: string;
    tacAccepted: boolean;
    selectedCountry: CountryModel;
    /** A user with a `parentsub` joins an existing customer — see `addressRules` below. */
    isSecondary: boolean;
  },
) {
  // #233 (backend #736): street, zip and city are the customer's three NOT NULL address
  // lines, and the backend answers a coded 400 for every one of them it does not get. The
  // form asks for all three up front instead of letting the user find out from a failed
  // save — but only where it shows them: a secondary user joins an existing customer whose
  // address is already set, and the template hides those three rows, so a required rule
  // there would hold the form invalid with no field on screen to fix.
  const addressRules = data.isSecondary ? [] : [Validators.required];

  return fb.group({
    // #165: the salutation („Herr", „Frau", „Firma") — the postal address is street/zip/city
    // below. Required, matching ProfileCreateModel's [Required] and the required-error block
    // this template has always carried — and customer-edit, which validates the same field
    // the same way.
    // backend #682: the value is one of four codes now, so the length rule is gone (no code
    // comes near 50 characters) and salutationCodeValidator refuses at the field what the
    // backend would refuse as `salutationInvalid`.
    salutation: [data.appProfile.salutation, [Validators.required, salutationCodeValidator]],
    // Backend #670: for a primary registrant this is the customer's CONTACT address
    // (published on the seller page, printed on invoices, receives seller order notices),
    // prefilled from the login and editable. The login itself never comes from the form.
    email: [data.email, [Validators.required, Validators.maxLength(320), Validators.email]],
    city: [data.appProfile.city, [...addressRules, Validators.maxLength(100)]],
    companyName: [data.appProfile.companyName, [Validators.maxLength(100)]],
    country: [data.appProfile.countryId, [Validators.maxLength(100)]],
    firstName: [data.appProfile.firstName, [Validators.maxLength(100)]],
    lastName: [data.appProfile.lastName, [Validators.maxLength(100)]],
    middleName: [data.appProfile.middleName, [Validators.maxLength(100)]],
    street: [data.appProfile.street, [...addressRules, Validators.maxLength(100)]],
    subject: [data.subject, [Validators.required, Validators.maxLength(50)]],
    title: [data.appProfile.title, [Validators.maxLength(100)]],
    zip: [data.appProfile.zip, [...addressRules, Validators.maxLength(50)]],
    tac: [data.tac, [Validators.required]],
    acceptTaC: [data.tacAccepted, [Validators.requiredTrue]],
    // The id — this fell back to the country object itself (#199).
    countryId: [data.appProfile.countryId || data.selectedCountry?.id, Validators.required],
  });
}
type RegistrationForm = ReturnType<typeof buildRegistrationForm>;

@Component({
    imports: [ReactiveFormsModule, CommonModule, KENDO_DROPDOWNS, AsyncClickDirective, FormRowComponent, FieldErrorsComponent],
    selector: 'app-welcome-app-data',
    templateUrl: './welcome-app-data.component.html',
    styleUrls: ['./welcome-app-data.component.css']
})
export class WelcomeAppDataComponent implements OnInit {
  fb = inject(FormBuilder);
  private router = inject(Router);
  appProfileService = inject(AppProfileService);
  private authService = inject(AuthorizeService);
  private location = inject(Location);
  private countryService = inject(CountryService);
  private callApiService = inject(CallApiService);
  private localeId = inject(LOCALE_ID);

  // The e-mail field sits outside a form row (it is replaced by text for a secondary user).
  protected readonly fieldErrorsDue = errorsDue;

  tac: string;
  tacAccepted: boolean;
  subject: string;
  appProfileForm: RegistrationForm;
  appProfile: ProfileCreateModel;
  submitted = false;
  email: string;
  invalidUserMessage: string;
  private destroyRef = inject(DestroyRef);
  public countries: CountryModel[];
  /** #178: the same list, labelled from `code2` in the language on screen. */
  public countryItems: CountryItem[] = [];
  public selectedCountry: CountryModel;
  public user: IUser;

  get f() {
    return this.appProfileForm?.controls;
  }

  ngOnInit() {
    this.appProfile = new ProfileCreateModel();
    // No refreshCurrentProfile() here: the guard/login flow has already fetched the
    // (missing) profile to get the user onto this page. Re-triggering the fetch while
    // the profile is null flips the app shell into its blocking spinner, which destroys
    // and re-creates this component in an endless loop. The refresh that matters runs
    // after the profile is created in submitForm().

    this.authService
      .getUser()
      .pipe(take(1))
      .subscribe((u) => {
        this.user = u;

        this.subject = u.sub;
        this.email = u.email;
        this.appProfile.firstName = u.given_name;
        this.appProfile.lastName = u.family_name;
        this.appProfile.middleName = u.middle_name;

        // Both before the form is built (#199): the country default used to race the terms.
        forkJoin({ tac: this.appProfileService.getDefaultTaC(), countries: this.countryService.get() })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(({ tac, countries }: { tac: string; countries: CountryModel[] }) => {
            this.tac = tac;
            this.countries = countries;
            this.countryItems = toCountryItems(countries, this.localeId);
            this.selectedCountry = countries && countries.length > 0 ? countries[0] : null;
            this.appProfile.countryId = this.selectedCountry.id;
            this.createForm();
          });
      });
  }

  /** backend #682: the Anrede picker. Computed once per form, not per change-detection run —
   *  a legacy value adds an option and would otherwise hand the template a new array each pass. */
  public salutationOptions = salutationOptionsFor(null);

  createForm() {
    // Do not take subject and email from form data for security reasons.
    // Select the first country if available
    this.salutationOptions = salutationOptionsFor(this.appProfile.salutation);

    this.appProfileForm = buildRegistrationForm(this.fb, {
      appProfile: this.appProfile,
      email: this.email,
      subject: this.subject,
      tac: this.tac,
      tacAccepted: this.tacAccepted,
      selectedCountry: this.selectedCountry,
      isSecondary: !!this.user?.parentsub,
    });

    // A secondary user joins an existing customer and sets no contact address — the field
    // only shows their login, read-only.
    if (this.user?.parentsub) {
      this.f['email'].disable();
    }
  }

  submitForm(): Observable<unknown> {
    if (!submittable(this.appProfileForm)) {
      return EMPTY;
    }
    this.appProfile.salutation = this.appProfileForm.controls.salutation.value;
    this.appProfile.city = this.appProfileForm.controls.city.value;
    this.appProfile.companyName = this.appProfileForm.controls.companyName.value;
    this.appProfile.countryId = this.appProfileForm.controls.countryId.value;
    this.appProfile.firstName = this.appProfileForm.controls.firstName.value;
    this.appProfile.lastName = this.appProfileForm.controls.lastName.value;
    this.appProfile.middleName = this.appProfileForm.controls.middleName.value;
    this.appProfile.street = this.appProfileForm.controls.street.value;
    this.appProfile.subject = this.subject;
    this.appProfile.title = this.appProfileForm.controls.title.value;
    this.appProfile.zip = this.appProfileForm.controls.zip.value;
    this.appProfile.acceptedTac = this.appProfileForm.controls.tac.value;
    this.submitted = true;

    // Secondary users (with a parentsub) are auto-approved; both branches create
    // the profile then refresh + navigate home.
    const isSecondary = !!this.user.parentsub;
    if (isSecondary) {
      this.appProfile.status = 'Approved';
      this.appProfile.role = 'Default';
    } else {
      // #670: the customer's contact address. A blank value lets the backend fall back to
      // the login address, so the customer always ends up with one.
      this.appProfile.email = (this.appProfileForm.controls.email.value ?? '').trim();
    }

    return this.callApiService
      .callWithToasts$(
        this.appProfileService.create(this.appProfile),
        isSecondary
          ? $localize`Das Benutzerprofil wurde aktualisiert.`
          : $localize`Das Profil wurde aktualisiert.`,
        isSecondary
          ? $localize`Beim Aktualisieren des Benutzerprofils ist ein Fehler aufgetreten.`
          : $localize`Beim Aktualisieren des Profils ist ein Fehler aufgetreten.`,
        // #178: a coded field error (salutationInvalid, nameRequired, maximumLength …) is
        // placed at the control it names instead of becoming a toast that names no field —
        // #159's original complaint.
        this.appProfileForm
      )
      .pipe(
        tap(() => {
          this.appProfileService.refreshCurrentProfile();
          this.router.navigateByUrl('/');
        })
      );
  }

  onBackClicked() {
    this.location.back();
  }
}
